import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { apiRequest } from '@/lib/apiClient';

type VerificationState = 'verifying' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerificationState>('verifying');
  const [message, setMessage] = useState('Verifying your email…');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setState('error');
      setMessage('This verification link is missing its token.');
      return;
    }

    let cancelled = false;
    apiRequest<{ verified: boolean }>('/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
      .then(() => {
        if (cancelled) return;
        setState('success');
        setMessage('Your email has been verified.');
      })
      .catch(() => {
        if (cancelled) return;
        setState('error');
        setMessage('This verification link is invalid, expired, or has already been used.');
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  return (
    <main className="from-brand-50 flex min-h-screen items-center justify-center bg-gradient-to-br to-slate-50 px-4 py-12">
      <section className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        {state === 'verifying' && <Loader2 className="text-brand-600 mx-auto h-12 w-12 animate-spin" />}
        {state === 'success' && <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />}
        {state === 'error' && <AlertCircle className="mx-auto h-12 w-12 text-red-600" />}

        <h1 className="mt-5 text-2xl font-bold text-slate-900">
          {state === 'verifying' ? 'Verifying email' : state === 'success' ? 'Email verified' : 'Verification failed'}
        </h1>
        <p className="mt-3 text-slate-600">{message}</p>

        {state !== 'verifying' && (
          <Link
            to={state === 'success' ? '/' : '/login'}
            className="from-brand-500 to-brand-700 mt-6 inline-flex rounded-lg bg-gradient-to-br px-5 py-2.5 font-semibold text-white"
          >
            {state === 'success' ? 'Continue to Jemmi' : 'Go to sign in'}
          </Link>
        )}
      </section>
    </main>
  );
}
